import React, { useState } from 'react';
import { useAttendance } from '../../hooks/useAttendance';
import { PDFReportGenerator } from '../../utils/pdfReportGenerator';
import { X, RefreshCw, Search, MessageSquare, FileText } from 'lucide-react';

interface AttendanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  meetingId: string;
  meetingName: string;
  meetingDate: string;
  meetingTime?: string;
}

export default function AttendanceModal({ 
  isOpen, 
  onClose, 
  meetingId, 
  meetingName, 
  meetingDate, 
  meetingTime 
}: AttendanceModalProps) {
  const { attendance, loading, error, updateAttendance, getAttendanceSummary } = useAttendance(meetingId);
  const [searchQuery, setSearchQuery] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);

  const summary = getAttendanceSummary();
  
  const filteredAttendance = attendance.filter(record =>
    record.members.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    record.members.phone.includes(searchQuery)
  );

  const handleAttendanceToggle = async (memberId: string, currentStatus: 'Present' | 'Absent') => {
    setUpdating(memberId);
    const newStatus = currentStatus === 'Present' ? 'Absent' : 'Present';
    
    const result = await updateAttendance(memberId, newStatus);
    if (result?.success) {
      // Attendance will be refreshed automatically by the hook
    } else {
      alert(result?.error || 'Failed to update attendance');
    }
    
    setUpdating(null);
  };

  const exportPDFReport = () => {
    const presentRecords = attendance.filter(r => r.status === 'Present').map(r => ({
      name: r.members?.name || 'Unknown',
      phone: r.members?.phone
    }));
    const absentRecords = attendance.filter(r => r.status === 'Absent').map(r => ({
      name: r.members?.name || 'Unknown',
      phone: r.members?.phone
    }));

    PDFReportGenerator.generateSingleMeetingPDF({
      meetingTitle: meetingName,
      date: meetingDate,
      time: meetingTime,
      presentRecords,
      absentRecords
    });
  };

  const shareWhatsAppSummary = () => {
    const presentRecords = attendance.filter(r => r.status === 'Present');
    const absentRecords = attendance.filter(r => r.status === 'Absent');
    const total = attendance.length;
    const presentCount = presentRecords.length;
    const absentCount = absentRecords.length;
    const percent = total > 0 ? ((presentCount / total) * 100).toFixed(1) : '0';

    let text = `📊 *MEETING ATTENDANCE SUMMARY*\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    text += `📌 *Meeting:* ${meetingName}\n`;
    text += `📅 *Date:* ${meetingDate}${meetingTime ? `  🕒 *Time:* ${meetingTime}` : ''}\n`;
    text += `\n👥 *ATTENDANCE STATS:*\n`;
    text += `• Total Members: *${total}*\n`;
    text += `• ✅ Present: *${presentCount}* (${percent}%)\n`;
    text += `• ❌ Absent: *${absentCount}*\n`;

    if (absentRecords.length > 0) {
      text += `\n📝 *ABSENTEE LIST (${absentRecords.length}):*\n`;
      absentRecords.forEach((r, idx) => {
        const name = r.members?.name || 'Unknown';
        const phone = r.members?.phone ? ` (${r.members.phone})` : '';
        text += `${idx + 1}. ${name}${phone}\n`;
      });
    }

    text += `\n━━━━━━━━━━━━━━━━━━━━\n`;
    text += `*State Meeting Tracker | SSF Kerala*`;

    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 transition-opacity">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-4xl max-h-[92vh] sm:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200">
        {/* Mobile Pull Indicator */}
        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto my-2 sm:hidden shrink-0" />

        {/* Header */}
        <div className="px-4 sm:px-6 py-3.5 border-b border-gray-200 shrink-0">
          <div className="flex items-center justify-between">
            <div className="min-w-0 pr-2">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{meetingName}</h2>
              <p className="text-xs sm:text-sm text-gray-500">{meetingDate} {meetingTime && `• ${meetingTime}`}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition text-gray-400 hover:text-gray-600 shrink-0"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Attendance Stats Cards */}
        <div className="px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200 grid grid-cols-4 gap-2 text-center text-xs shrink-0">
          <div className="bg-white p-2 rounded-xl border border-gray-200">
            <p className="text-gray-500 text-[10px] sm:text-xs">TOTAL</p>
            <p className="font-bold text-sm sm:text-base text-gray-900">{summary.total}</p>
          </div>
          <div className="bg-white p-2 rounded-xl border border-gray-200">
            <p className="text-green-600 text-[10px] sm:text-xs font-semibold">PRESENT</p>
            <p className="font-bold text-sm sm:text-base text-green-700">{summary.present}</p>
          </div>
          <div className="bg-white p-2 rounded-xl border border-gray-200">
            <p className="text-red-600 text-[10px] sm:text-xs font-semibold">ABSENT</p>
            <p className="font-bold text-sm sm:text-base text-red-700">{summary.absent}</p>
          </div>
          <div className="bg-white p-2 rounded-xl border border-gray-200">
            <p className="text-blue-600 text-[10px] sm:text-xs font-semibold">RATE</p>
            <p className="font-bold text-sm sm:text-base text-blue-700">{summary.percentage}%</p>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-100 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search member name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
            />
          </div>
        </div>

        {/* Member List */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="text-center py-12 text-gray-500 text-sm">
              <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-blue-600" />
              Loading member attendance records...
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600 text-sm">{error}</div>
          ) : filteredAttendance.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">No members found.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredAttendance.map((record) => {
                const isPresent = record.status === 'Present';
                const isBusy = updating === record.member_id;

                return (
                  <div 
                    key={record.id}
                    className={`p-3.5 rounded-xl border transition flex items-center justify-between ${
                      isPresent 
                        ? 'bg-green-50/60 border-green-200' 
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="min-w-0 pr-2">
                      <p className="font-semibold text-gray-900 text-xs sm:text-sm truncate">{record.members.name}</p>
                      <p className="text-[11px] text-gray-500">{record.members.phone}</p>
                    </div>

                    <div className="flex items-center space-x-1.5 shrink-0">
                      <button
                        onClick={() => handleAttendanceToggle(record.member_id, record.status)}
                        disabled={isBusy}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center space-x-1 ${
                          isPresent 
                            ? 'bg-green-600 text-white shadow-2xs' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        } ${isBusy ? 'opacity-50' : ''}`}
                      >
                        {isBusy && !isPresent ? (
                          <RefreshCw size={14} className="animate-spin" />
                        ) : (
                          <span>Present</span>
                        )}
                      </button>

                      <button
                        onClick={() => handleAttendanceToggle(record.member_id, record.status)}
                        disabled={isBusy}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center space-x-1 ${
                          !isPresent 
                            ? 'bg-red-600 text-white shadow-2xs' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        } ${isBusy ? 'opacity-50' : ''}`}
                      >
                        {isBusy && isPresent ? (
                          <RefreshCw size={14} className="animate-spin" />
                        ) : (
                          <span>Absent</span>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center space-x-2">
              <button
                onClick={exportPDFReport}
                disabled={attendance.length === 0}
                className="flex items-center space-x-1.5 px-3 sm:px-4 py-2 bg-red-600 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-red-700 transition disabled:opacity-50 min-h-[40px] shadow-2xs"
              >
                <FileText size={16} />
                <span>Export PDF</span>
              </button>
              <button
                onClick={shareWhatsAppSummary}
                disabled={attendance.length === 0}
                className="flex items-center space-x-1.5 px-3 sm:px-4 py-2 bg-green-600 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-green-700 transition disabled:opacity-50 min-h-[40px] shadow-2xs"
              >
                <MessageSquare size={16} />
                <span className="hidden sm:inline">Share Summary to WhatsApp</span>
                <span className="sm:hidden">WhatsApp</span>
              </button>
            </div>

            <button
              onClick={onClose}
              className="px-5 py-2 bg-gray-900 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-gray-800 transition min-h-[40px]"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
